import { useState, useRef, useCallback } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, X, Loader2, QrCode, Globe, CreditCard, FileText,
  Check, ChevronRight, User, Mail, Phone, MapPin, Building2,
  Sparkles, RotateCcw, ImagePlus,
} from "lucide-react";

const SOURCE_ICONS = {
  business_card: { icon: CreditCard, label: "Business Card", color: "text-purple-400 bg-purple-400/10" },
  qr_code: { icon: QrCode, label: "QR Code", color: "text-green-400 bg-green-400/10" },
  image_url: { icon: Globe, label: "URL from Image", color: "text-cyan-400 bg-cyan-400/10" },
  image_text: { icon: FileText, label: "Text from Image", color: "text-amber-400 bg-amber-400/10" },
};

export default function TradeFairCapture({ token, onCreated, onClose }) {
  const [step, setStep] = useState("capture"); // capture | analyzing | review
  const [frontFile, setFrontFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Review form fields
  const [hotelName, setHotelName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactRole, setContactRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [sourceType, setSourceType] = useState("image_text");
  const [note, setNote] = useState("");

  const frontRef = useRef(null);
  const backRef = useRef(null);
  const h = { Authorization: `Bearer ${token}` };

  const handleFileSelect = useCallback((file, side) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (side === "front") {
        setFrontFile(file);
        setFrontPreview(e.target.result);
      } else {
        setBackFile(file);
        setBackPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const analyzeImage = useCallback(async () => {
    if (!frontFile) { toast.error("Please take or select a photo first"); return; }
    setStep("analyzing");
    try {
      const fd = new FormData();
      fd.append("file", frontFile);
      const res = await axios.post(`${API}/image/analyze`, fd, {
        headers: { ...h, "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      const d = res.data;
      setAnalysis(d);
      setHotelName(d.company_name || "");
      setContactName(d.contact_name || "");
      setContactRole(d.role || "");
      setEmail(d.email || "");
      setPhone(d.phone || "");
      setWebsite(d.website || "");
      setAddress(d.address || "");
      setSourceType(d.detected_source || "image_text");
      setStep("review");
    } catch (err) {
      toast.error("Analysis failed. Please try again or enter data manually.");
      setStep("review");
    }
  }, [frontFile, h]);

  const handleSubmit = useCallback(async () => {
    if (!hotelName.trim()) { toast.error("Hotel name is required"); return; }
    setSubmitting(true);
    try {
      const params = new URLSearchParams({ hotel_name: hotelName.trim() });
      if (contactName) params.set("contact_name", contactName.trim());
      if (contactRole) params.set("contact_role", contactRole.trim());
      if (email) params.set("contact_email", email.trim());
      if (phone) params.set("contact_phone", phone.trim());
      if (website) params.set("website", website.trim());
      if (address) params.set("address", address.trim());
      params.set("source_type", sourceType);
      if (note) params.set("note", note.trim());

      const fd = new FormData();
      if (frontFile) fd.append("front", frontFile);
      if (backFile) fd.append("back", backFile);

      await axios.post(`${API}/prospects/from-capture?${params.toString()}`, fd, {
        headers: { ...h, "Content-Type": "multipart/form-data" },
      });
      toast.success("Prospect added to pipeline!");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create prospect");
    } finally { setSubmitting(false); }
  }, [hotelName, contactName, contactRole, email, phone, website, address, sourceType, note, frontFile, backFile, h, onCreated]);

  const resetCapture = () => {
    setStep("capture");
    setFrontFile(null); setFrontPreview(null);
    setBackFile(null); setBackPreview(null);
    setAnalysis(null);
    setHotelName(""); setContactName(""); setContactRole("");
    setEmail(""); setPhone(""); setWebsite(""); setAddress("");
    setSourceType("image_text"); setNote("");
  };

  const src = SOURCE_ICONS[sourceType] || SOURCE_ICONS.image_text;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="trade-fair-capture">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md max-h-[92vh] bg-[#0A0A0A] border border-white/[0.08] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col shadow-2xl" data-testid="capture-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C9A96E]" />
            <span className="text-white font-semibold text-sm">
              {step === "capture" ? "Quick Capture" : step === "analyzing" ? "Analyzing..." : "Review & Create"}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 text-white/30 hover:text-white active:scale-95 rounded-lg" data-testid="capture-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">

          {/* ═══ STEP 1: CAPTURE ═══ */}
          {step === "capture" && (
            <div className="p-4" data-testid="capture-step">
              <input ref={frontRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0], "front")} data-testid="capture-front-input" />
              <input ref={backRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0], "back")} data-testid="capture-back-input" />

              {/* Main capture area */}
              {!frontPreview ? (
                <button
                  onClick={() => frontRef.current?.click()}
                  className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-white/[0.1] bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#C9A96E]/30 transition-all flex flex-col items-center justify-center gap-3 active:scale-[0.98]"
                  data-testid="capture-main-btn"
                >
                  <div className="w-16 h-16 rounded-2xl bg-[#C9A96E]/10 flex items-center justify-center">
                    <Camera className="w-8 h-8 text-[#C9A96E]" />
                  </div>
                  <div className="text-center">
                    <p className="text-white/70 font-medium text-sm">Take Photo or Choose Image</p>
                    <p className="text-white/25 text-xs mt-1">Business card, QR code, booth sign, poster...</p>
                  </div>
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="relative rounded-xl overflow-hidden border border-white/[0.08]">
                    <img src={frontPreview} alt="Captured" className="w-full max-h-[260px] object-contain bg-black/30" data-testid="capture-preview" />
                    <div className="absolute top-2 left-2 flex gap-1.5">
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-black/60 text-white/80 backdrop-blur-sm">Front</span>
                    </div>
                    <button onClick={() => { setFrontFile(null); setFrontPreview(null); }} className="absolute top-2 right-2 p-1.5 bg-black/60 text-white/60 hover:text-white rounded-lg backdrop-blur-sm" data-testid="capture-remove-front">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Back side (optional) */}
                  {!backPreview ? (
                    <button onClick={() => backRef.current?.click()} className="w-full py-3 rounded-xl border border-dashed border-white/[0.06] text-white/25 text-xs hover:bg-white/[0.02] hover:border-white/[0.1] transition-all flex items-center justify-center gap-2 active:scale-[0.98]" data-testid="capture-add-back">
                      <ImagePlus className="w-4 h-4" /> Add back side (optional)
                    </button>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden border border-white/[0.08]">
                      <img src={backPreview} alt="Back" className="w-full max-h-[140px] object-contain bg-black/30" />
                      <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-black/60 text-white/80 backdrop-blur-sm">Back</span>
                      <button onClick={() => { setBackFile(null); setBackPreview(null); }} className="absolute top-2 right-2 p-1.5 bg-black/60 text-white/60 hover:text-white rounded-lg backdrop-blur-sm">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {frontPreview && (
                <div className="mt-4 space-y-2">
                  <button onClick={analyzeImage} className="w-full py-3.5 bg-[#C9A96E] text-white rounded-xl font-semibold text-sm hover:bg-[#C9A96E]/90 transition-colors flex items-center justify-center gap-2 active:scale-[0.98]" data-testid="capture-analyze-btn">
                    <Sparkles className="w-4 h-4" /> Analyze & Extract
                  </button>
                  <button onClick={() => { setAnalysis(null); setStep("review"); }} className="w-full py-2.5 text-white/30 text-xs hover:text-white/50 transition-colors" data-testid="capture-skip-btn">
                    Skip analysis — enter manually
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ═══ STEP 2: ANALYZING ═══ */}
          {step === "analyzing" && (
            <div className="p-8 flex flex-col items-center justify-center min-h-[300px]" data-testid="analyzing-step">
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-2xl bg-[#C9A96E]/10 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-[#C9A96E] animate-pulse" />
                </div>
                <Loader2 className="w-6 h-6 text-[#C9A96E] animate-spin absolute -bottom-1 -right-1" />
              </div>
              <p className="text-white font-semibold text-sm mb-1">Analyzing image...</p>
              <p className="text-white/30 text-xs text-center">Detecting QR codes, extracting text,<br />identifying contacts and URLs</p>
            </div>
          )}

          {/* ═══ STEP 3: REVIEW ═══ */}
          {step === "review" && (
            <div className="p-4" data-testid="review-step">
              {/* Detection badge */}
              {analysis && (
                <div className="mb-4 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="detection-result">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${src.color}`}>
                      <src.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-white text-xs font-semibold">{src.label}</span>
                      <span className={`ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${analysis.confidence === "high" ? "bg-green-500/10 text-green-400" : analysis.confidence === "medium" ? "bg-amber-500/10 text-amber-400" : "bg-white/5 text-white/30"}`}>
                        {analysis.confidence} confidence
                      </span>
                    </div>
                  </div>
                  {analysis.qr_detected && analysis.qr_url && (
                    <p className="text-cyan-400/70 text-[11px] flex items-center gap-1 mt-1"><QrCode className="w-3 h-3" /> {analysis.qr_url}</p>
                  )}
                </div>
              )}

              {/* Form fields */}
              <div className="space-y-2.5">
                <div>
                  <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold mb-1 block">Hotel / Company *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/15" />
                    <input value={hotelName} onChange={e => setHotelName(e.target.value)} placeholder="Hotel name" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl pl-10 pr-3 py-2.5 outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30 transition-colors" data-testid="review-hotel-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold mb-1 block">Contact</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/15" />
                      <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Name" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl pl-9 pr-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-contact-name" />
                    </div>
                  </div>
                  <div>
                    <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold mb-1 block">Role</label>
                    <input value={contactRole} onChange={e => setContactRole(e.target.value)} placeholder="Title" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-contact-role" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/15" />
                    <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl pl-9 pr-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-email" />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/15" />
                    <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl pl-9 pr-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-phone" />
                  </div>
                </div>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/15" />
                  <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="Website" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl pl-9 pr-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-website" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/15" />
                  <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Location / Address" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl pl-9 pr-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-address" />
                </div>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" className="w-full bg-white/[0.04] border border-white/[0.06] text-white text-sm rounded-xl px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors" data-testid="review-note" />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {step === "review" && (
          <div className="p-4 border-t border-white/[0.06] shrink-0 space-y-2">
            <button
              onClick={handleSubmit}
              disabled={submitting || !hotelName.trim()}
              className="w-full py-3.5 bg-[#C9A96E] text-white rounded-xl font-semibold text-sm hover:bg-[#C9A96E]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-30 active:scale-[0.98]"
              data-testid="review-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {submitting ? "Creating..." : "Add to Pipeline"}
            </button>
            <button onClick={resetCapture} className="w-full py-2 text-white/25 text-xs hover:text-white/40 transition-colors flex items-center justify-center gap-1.5">
              <RotateCcw className="w-3 h-3" /> Start over
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
